import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { protectedResources } from '../auth-config';
import { IntegrationResponseMessage } from '../models/integration';
import { IntegrationIdentifier } from '../models/integration-identifier';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ToastService } from './toast.service';
import { IntegrationInformation } from '../models/integration-information';

@Injectable()
export class IdentifierService {
  private baseUrl: string = protectedResources.LOSTalkerAPI.endpoint;
  private actionUrl = '';
  private Url = '';
  private headers = new HttpHeaders();

  private errorHandler(error: HttpErrorResponse, ts: ToastService): Observable<any> {
    ts.toastCreate('An HTTP transport error occured: ' + error.url + ' ' + error.status, 'Warning', {
      keepAfterRouteChange: true,
    });

    if (environment.enableDebuggingTools) {
      console.error('An HTTP transport error occured: ' + error.error);
    }

    return throwError(() => error);
  }

  constructor(private http: HttpClient, private toastService: ToastService) { }

  //--------------------------- Integration Identifiers ------------------------------------------//

  // Get All Integration Identifiers
  getAllIntegrationIdentifiers(): Observable<IntegrationIdentifier[]> {
    this.actionUrl = 'IntegrationIdentifiers/';
    this.Url = this.baseUrl.concat(this.actionUrl);

    return this.http
      .get<IntegrationIdentifier[]>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  // Get All Active Integration Identifiers
  getAllActiveIntegrationIdentifiers(): Observable<IntegrationIdentifier[]> {
    this.actionUrl = 'IntegrationIdentifiers/ActiveAll';
    this.Url = this.baseUrl.concat(this.actionUrl);

    return this.http
      .get<IntegrationIdentifier[]>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  getAllActiveIntegrationIdentifiersForFastFetch(): Observable<IntegrationInformation[]> {
    this.actionUrl = 'IntegrationIdentifiers/ActiveFastFetch';
    this.Url = this.baseUrl.concat(this.actionUrl);

    return this.http
      .get<IntegrationInformation[]>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  getAllActiveIntegrationIdentifiersForDataSwap(): Observable<IntegrationInformation[]> {
    this.actionUrl = 'IntegrationIdentifiers/ActiveDataSwap';
    this.Url = this.baseUrl.concat(this.actionUrl);

    return this.http
      .get<IntegrationInformation[]>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  getAllIntegrationInformations(): Observable<IntegrationInformation[]> {  
    this.actionUrl = 'IntegrationInformations/All';
    this.Url = this.baseUrl.concat(this.actionUrl);

    return this.http
      .get<IntegrationInformation[]>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  getAllActiveIntegrationInformation(): Observable<IntegrationInformation[]> {  
    this.actionUrl = 'IntegrationInformations/ActiveInformation';
    this.Url = this.baseUrl.concat(this.actionUrl);

    return this.http
      .get<IntegrationInformation[]>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  getAllActiveIntegrationInformationWithTokenInfo(): Observable<IntegrationInformation[]> {
    this.actionUrl = 'IntegrationInformations/AllWithTokenInfo';
    this.Url = this.baseUrl.concat(this.actionUrl);

    return this.http
      .get<IntegrationInformation[]>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  getIntegrationInformationsByClientInformation(clientInformationId: string): Observable<IntegrationInformation[]> {
    this.actionUrl = 'IntegrationInformations/ByClientInformation/';
    this.Url = this.baseUrl.concat(this.actionUrl, clientInformationId);

    return this.http
      .get<IntegrationInformation[]>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  getIntegrationInformationsByDestination(destinationEndpointId: number, identifierIdValue: string): Observable<IntegrationInformation[]> {
    this.actionUrl = 'IntegrationInformations/Match/';
    this.Url = this.baseUrl.concat(this.actionUrl, destinationEndpointId.toString(), "/", identifierIdValue);

    return this.http
      .get<IntegrationInformation[]>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  // GET Integration Information By Id and Destination
  getIntegrationInformationByIntegrationAndDestination(integrationId: string, destinationId: number): Observable<IntegrationInformation> {
    this.actionUrl = 'IntegrationInformation/';
    this.Url = this.baseUrl.concat(this.actionUrl, integrationId, '/', destinationId.toString());

    return this.http
      .get<IntegrationInformation>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  // GET Integration Identifiers By Id and Destination
  getIntegrationIdentifierByIntegrationAndDestination(integrationId: string, destinationName: string): Observable<IntegrationIdentifier> {
    this.actionUrl = 'IntegrationIdentifier/';
    this.Url = this.baseUrl.concat(this.actionUrl, integrationId, '/', destinationName);

    return this.http
      .get<IntegrationIdentifier>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  // GET Integration Identifiers By Id and Destination
  getIntegrationIdentifierByIntegrationAndDestinationId(integrationId: string, destinationId: number): Observable<IntegrationIdentifier> {
    this.actionUrl = 'IntegrationIdentifier/';
    this.Url = this.baseUrl.concat(this.actionUrl, 'ById/', integrationId, '/', destinationId.toString());

    return this.http
      .get<IntegrationIdentifier>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  // Get Integration Identifiers by Integration Id
  getIntegrationIdentifiers(integrationId: string): Observable<IntegrationIdentifier[]> {
    this.actionUrl = 'IntegrationIdentifiers/';
    this.Url = this.baseUrl.concat(this.actionUrl, integrationId);

    return this.http
      .get<IntegrationIdentifier[]>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  // Get Integration Identifiers by Client Id
  getIntegrationIdentifiersByClient(clientId: number): Observable<IntegrationIdentifier[]> {
    this.actionUrl = 'IntegrationIdentifiers/ByClient/';
    this.Url = this.baseUrl.concat(this.actionUrl, clientId.toString());

    return this.http
      .get<IntegrationIdentifier[]>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  // Get Integration Identifiers by Client Information Id
  getIntegrationIdentifiersByClientInformation(clientInformationId: string): Observable<IntegrationIdentifier[]> {
    this.actionUrl = 'IntegrationIdentifiers/ByClientInformation/';
    this.Url = this.baseUrl.concat(this.actionUrl, clientInformationId);

    return this.http
      .get<IntegrationIdentifier[]>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }
  
  generateIntegrationWorkbook(identifier: IntegrationIdentifier): Observable<Blob> {
    this.actionUrl = 'LambdaUtility/';
    this.Url = this.baseUrl.concat(this.actionUrl, identifier.integrationId, '/', identifier.destinationId.toString());
    //Blob
    return this.http
      .get(this.Url, { responseType: 'blob' })
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  downloadExcelResult(blob: Blob, identifier: IntegrationIdentifier) : void{
    const a = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    a.href = objectUrl;
    a.download = identifier.identifierIdValue+'-'+identifier.identifierNameValue+'_'+ identifier.destinationName+'.xlsx'; // default filename
    a.click();
    URL.revokeObjectURL(objectUrl);
  }

  // DELETE Integration Identifier
  deleteIntegrationIdentifier(identifier: IntegrationIdentifier): Observable<IntegrationIdentifier> {
    this.actionUrl = 'IntegrationIdentifier/';
    this.Url = this.baseUrl.concat(
      this.actionUrl,
      identifier.integrationId,
      '/',
      identifier.destinationId.toString(),
    );

    return this.http
      .delete<IntegrationIdentifier>(this.Url, { headers: this.headers })
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  // PUT Update Integration Identifier
  updateIntegrationIdentifier(integrationIdentifier: IntegrationIdentifier): Observable<IntegrationIdentifier> {
    this.actionUrl = 'IntegrationIdentifier/';
    this.Url = this.baseUrl.concat(this.actionUrl);
    this.headers.append('Content-Type', 'application/json');

    return this.http
      .put<IntegrationIdentifier>(this.Url, integrationIdentifier, { headers: this.headers })
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  // POST Insert Integration Identifier
  addIntegrationIdentifier(integrationIdentifier: IntegrationIdentifier): Observable<IntegrationResponseMessage> {
    this.actionUrl = 'IntegrationIdentifier/';
    this.Url = this.baseUrl.concat(this.actionUrl);
    this.headers.append('Content-Type', 'application/json');

    return this.http
      .post<IntegrationResponseMessage>(this.Url, integrationIdentifier, { headers: this.headers })
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

}
